import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { adminApi } from '../services/api'
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  Clock,
  Loader2,
  Building2
} from 'lucide-react'

// Status Badge
const StatusBadge = ({ status }) => {
  const styles = {
    trial: 'bg-blue-100 text-blue-700',
    active: 'bg-green-100 text-green-700',
    suspended: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-red-100 text-red-700',
    deleted: 'bg-gray-100 text-gray-700'
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.trial}`}>
      {status}
    </span>
  )
}

// Plan Badge
const PlanBadge = ({ plan }) => {
  const styles = {
    trial: 'bg-gray-100 text-gray-700',
    starter: 'bg-blue-100 text-blue-700',
    professional: 'bg-purple-100 text-purple-700',
    enterprise: 'bg-amber-100 text-amber-700'
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[plan] || styles.trial}`}>
      {plan}
    </span>
  )
}

export default function TenantsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [tenants, setTenants] = useState([])
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [status, setStatus] = useState(searchParams.get('status') || '')
  const [plan, setPlan] = useState(searchParams.get('plan') || '')

  useEffect(() => {
    loadTenants()
  }, [searchParams])

  const loadTenants = async () => {
    try {
      setLoading(true)
      const params = {
        page: searchParams.get('page') || 1,
        limit: 20,
        search: searchParams.get('search') || undefined,
        status: searchParams.get('status') || undefined,
        plan: searchParams.get('plan') || undefined
      }

      const response = await adminApi.getTenants(params)
      setTenants(response.data.data.tenants)
      setPagination(response.data.data.pagination)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    const params = new URLSearchParams(searchParams)
    if (search) params.set('search', search)
    else params.delete('search')
    if (status) params.set('status', status)
    else params.delete('status')
    if (plan) params.set('plan', plan)
    else params.delete('plan')
    params.set('page', '1')
    setSearchParams(params)
  }

  const changePage = (newPage) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', newPage.toString())
    setSearchParams(params)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Centri</h1>
          <p className="text-gray-500">Gestisci tutti i centri registrati</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1 min-w-full sm:min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca per nome, email, slug..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Status filter */}
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutti gli stati</option>
            <option value="trial">Trial</option>
            <option value="active">Attivo</option>
            <option value="suspended">Sospeso</option>
            <option value="cancelled">Cancellato</option>
          </select>

          {/* Plan filter */}
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutti i piani</option>
            <option value="trial">Trial</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>

          <button
            type="submit"
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtra
          </button>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : tenants.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Building2 className="h-12 w-12 mb-4 text-gray-300" />
            <p>Nessun centro trovato</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Centro
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organizzazione
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Piano
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo Ente
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registrato
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scadenza Trial
                  </th>
                  <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tenants.map((tenant) => (
                  <tr key={tenant._id} className="hover:bg-gray-50">
                    <td className="px-3 md:px-6 py-4">
                      <div className="min-w-0 max-w-[160px] sm:max-w-none">
                        <p className="font-medium text-gray-900 truncate">{tenant.name}</p>
                        <p className="text-sm text-gray-500 truncate">{tenant.contacts?.email}</p>
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4 text-sm">
                      {tenant.organizationId?.name ? (
                        <Link
                          to={`/organizations/${tenant.organizationId._id}`}
                          className="text-primary-600 hover:text-primary-700 hover:underline block max-w-[140px] sm:max-w-none truncate"
                        >
                          {tenant.organizationId.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={tenant.status} />
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <PlanBadge plan={tenant.currentPlan} />
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {tenant.entityType}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(tenant.registeredAt).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      {tenant.status === 'trial' && tenant.trialEndDate && (
                        <div className="flex items-center text-sm">
                          <Clock className="h-4 w-4 mr-1 text-gray-400" />
                          <span className={
                            new Date(tenant.trialEndDate) < new Date()
                              ? 'text-red-600'
                              : 'text-gray-500'
                          }>
                            {new Date(tenant.trialEndDate).toLocaleDateString('it-IT')}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right">
                      <Link
                        to={`/tenants/${tenant._id}`}
                        className="inline-flex items-center px-3 py-1 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        <span className="hidden sm:inline">Dettagli</span>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-3 md:px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2">
            <p className="text-xs sm:text-sm text-gray-500">
              Pagina {pagination.page} di {pagination.pages} ({pagination.total} totali)
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => changePage(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => changePage(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
